// #region Imports
import { Divider, List, ListItem, Button, ListItemSecondaryAction, Popover, ListItemText, styled } from 'MaterialUIComponents';
import { RefObject } from 'react';
import { NavLink } from 'react-router-dom';
import { bind, React } from '../Imports';
import { IUserManagementServiceInjectedProps, UserManagementService } from '$State/UserManagementFreezerService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import { VerifyRole, RolesEnum } from 'externals/VerifyRole';
import { ListItemTextProps, listItemTextClasses } from '@mui/material/ListItemText';
import { withNavigate, WithNavigateProps } from '$Components/hocs/withNavigate';
import { StandardProtectedRoutePaths } from '../router/route-sets/standard-route-config';
// #endregion Imports

const styles = require('./MainMenu.scss') as {
    menuActive: string;
    active: string;
    link: string;
    listItemText: string;
    logo: string;
    main: string;
    navbar: string;
    page: string;
    pageOuter: string;
    subMenuItem: string;
    toolbar: string;
};

const StyledListItemText = styled(({ ...props }: ListItemTextProps) => <ListItemText {...props} />)(({ theme }) => ({
    [`& .${listItemTextClasses.primary}`]: {
        fontWeight: 'inherit',
    },
}));

type IMainMenuProps = IUserManagementServiceInjectedProps;

interface IMainMenuState {
    isConfigurationOpen: boolean;
    configurationRef: RefObject<HTMLDivElement>;
    isLogoutInProcess: boolean;
}

export class _MainMenu extends React.Component<IMainMenuProps & WithNavigateProps, IMainMenuState> {
    state: IMainMenuState = {
        isConfigurationOpen: false,
        configurationRef: React.createRef<HTMLDivElement>(),
        isLogoutInProcess: false,
    };

    @bind
    toggleConfigurationOpen(e?: React.MouseEvent<Element>) {
        if (e) {
            e.stopPropagation();
            e.preventDefault();
        }

        this.setState((curState) => {
            return {
                isConfigurationOpen: !curState.isConfigurationOpen,
            };
        });
    }

    @bind
    closeConfiguration(e?: React.MouseEvent<Element>) {
        this.setState({
            isConfigurationOpen: false,
        });
    }

    @bind
    navigateTo(target: string) {
        return (e: React.MouseEvent<Element>) => {
            e.preventDefault();
            e.stopPropagation();
            this.props.history.push(target);
        };
    }

    @bind
    logout() {
        this.setState({ isLogoutInProcess: true });
        this.props.userManagement.logout();
    }

    render() {
        const { isConfigurationOpen, configurationRef, isLogoutInProcess } = this.state;

        return (
            <div className={styles.main}>
                <List>
                    <VerifyRole allowedRole={[RolesEnum.videoEventView, RolesEnum.videoEventEdit, RolesEnum.encompassVideo]}>
                        <NavLink
                            className={({ isActive }) => (isActive ? `${styles.link} ${styles.menuActive}` : styles.link)}
                            to={StandardProtectedRoutePaths.VideoEvents}
                        >
                            <ListItem button={true}>
                                <StyledListItemText inset={true}>Video Events</StyledListItemText>
                            </ListItem>
                        </NavLink>
                    </VerifyRole>

                    <VerifyRole allowedRole={[RolesEnum.videoRecallEdit, RolesEnum.encompassVideo]}>
                        <NavLink
                            className={({ isActive }) => (isActive ? `${styles.link} ${styles.menuActive}` : styles.link)}
                            to={StandardProtectedRoutePaths.VideoRecall}
                        >
                            <ListItem button={true}>
                                <StyledListItemText inset={true}>Video Recall</StyledListItemText>
                            </ListItem>
                        </NavLink>
                    </VerifyRole>

                    <VerifyRole allowedRole={[RolesEnum.videoAdmin, RolesEnum.encompassVideo]}>
                        <div ref={configurationRef}>
                            <ListItem button={true} onClick={this.toggleConfigurationOpen}>
                                <StyledListItemText inset={true}>Configuration</StyledListItemText>

                                <ListItemSecondaryAction onClick={this.toggleConfigurationOpen}>
                                    <FontAwesomeIcon icon={faChevronRight} />
                                </ListItemSecondaryAction>
                            </ListItem>

                            <Popover
                                anchorEl={configurationRef.current}
                                anchorOrigin={{
                                    horizontal: 'right',
                                    vertical: 'top',
                                }}
                                PaperProps={{
                                    style: { background: '#C6DEF2' },
                                }}
                                transformOrigin={{
                                    horizontal: 'left',
                                    vertical: 'top',
                                }}
                                onClose={() => this.toggleConfigurationOpen()}
                                open={isConfigurationOpen}
                            >
                                <List>
                                    <NavLink
                                        className={({ isActive }) => (isActive ? `${styles.link} ${styles.menuActive}` : styles.link)}
                                        to={StandardProtectedRoutePaths.Installers}
                                    >
                                        <ListItem button={true} onClick={this.closeConfiguration}>
                                            <StyledListItemText>Installers</StyledListItemText>
                                        </ListItem>
                                    </NavLink>

                                    <NavLink
                                        className={({ isActive }) => (isActive ? `${styles.link} ${styles.menuActive}` : styles.link)}
                                        to={StandardProtectedRoutePaths.DeviceAssociation}
                                    >
                                        <ListItem button={true} onClick={this.closeConfiguration}>
                                            <StyledListItemText>Device Associations</StyledListItemText>
                                        </ListItem>
                                    </NavLink>

                                    <NavLink
                                        className={({ isActive }) => (isActive ? `${styles.link} ${styles.menuActive}` : styles.link)}
                                        to={StandardProtectedRoutePaths.Settings}
                                    >
                                        <ListItem button={true} onClick={this.closeConfiguration}>
                                            <StyledListItemText>Settings</StyledListItemText>
                                        </ListItem>
                                    </NavLink>
                                </List>
                            </Popover>
                        </div>
                    </VerifyRole>

                    <VerifyRole allowedRole={[RolesEnum.videoEventView, RolesEnum.videoEventEdit, RolesEnum.encompassVideo]}>
                        <NavLink
                            className={({ isActive }) => (isActive ? `${styles.link} ${styles.menuActive}` : styles.link)}
                            to={StandardProtectedRoutePaths.VideoDashboard}
                        >
                            <ListItem button={true}>
                                <StyledListItemText inset={true}>Video Dashboard</StyledListItemText>
                            </ListItem>
                        </NavLink>
                    </VerifyRole>
                </List>
                <Divider />
                {this.props.userManagement.isLoggedIn() && (
                    <div style={{ textAlign: 'center', paddingTop: '20px' }}>
                        <Button size="small" disabled={isLogoutInProcess} onClick={() => this.logout()}>
                            Logout
                        </Button>
                    </div>
                )}
            </div>
        );
    }
}

const MainMenu = withNavigate(UserManagementService.inject(_MainMenu));

export { MainMenu };
